// PostMap — mini-map rendered under any post whose backend payload
// carries a `pins` array. Used by the crime_analyst's Weekly Watch,
// but generic: any post with pins gets one. MapLibre + OSM tiles,
// same stack as /admin/map + boundary editor.
//
// Failure modes: if pins is empty/malformed, nothing renders (the
// component returns null). No spinners, no errors — this is a
// progressive enhancement, not a load-bearing UI.

import { useEffect, useRef } from 'react';
import * as maplibregl from 'maplibre-gl';
import { type Map as MLMap, Marker, NavigationControl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export interface PostPin {
    lat:   number;
    lng:   number;
    label?: string;
}

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

interface Props {
    pins: PostPin[];
    height?: number;
}

export function PostMap({ pins, height = 260 }: Props) {
    const ref = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<MLMap | null>(null);

    useEffect(() => {
        if (!ref.current || pins.length === 0) return;
        const valid = pins.filter(p =>
            typeof p.lat === 'number' && typeof p.lng === 'number' &&
            p.lat !== 0 && p.lng !== 0);
        if (valid.length === 0) return;

        const center: [number, number] = [valid[0].lng, valid[0].lat];
        const m = new maplibregl.Map({
            container: ref.current,
            style: {
                version: 8,
                sources: {
                    osm: {
                        type: 'raster', tileSize: 256, tiles: [OSM_TILE_URL],
                        attribution: '© OpenStreetMap contributors',
                    },
                },
                layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
            } as maplibregl.StyleSpecification,
            center,
            zoom: 13,
            attributionControl: { compact: true },
        });
        m.addControl(new NavigationControl({ showCompass: false }), 'top-right');
        mapRef.current = m;

        const markers: Marker[] = [];
        const bounds = new maplibregl.LngLatBounds();
        for (const p of valid) {
            const el = document.createElement('div');
            el.className = 'post-map-pin';
            el.title = p.label ?? '';
            const marker = new maplibregl.Marker({ element: el })
                .setLngLat([p.lng, p.lat]);
            if (p.label) {
                marker.setPopup(new maplibregl.Popup({ closeButton: false, offset: 12 })
                    .setText(p.label));
            }
            marker.addTo(m);
            markers.push(marker);
            bounds.extend([p.lng, p.lat]);
        }
        if (valid.length > 1) {
            m.fitBounds(bounds, { padding: 30, animate: false });
        }
        return () => {
            for (const mk of markers) mk.remove();
            m.remove();
            mapRef.current = null;
        };
    }, [pins]);

    if (pins.length === 0) return null;
    return (
        <div className="post-map-wrap" style={{ margin: '12px 0' }}>
            <div ref={ref} style={{ width: '100%', height, border: '1px solid #cfc9b7' }} />
        </div>
    );
}
